import React from "react";
import { filter, find, get, includes, map, toNumber } from "lodash";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Column, Row } from "@/shared/ui/Blocks";
import { IconButton } from "@/shared/ui/IconButton";
import { ModalFileName } from "@/shared/modals";
import { SubtitleText } from "@/shared/ui/Typography";
import { Title } from "@/shared/ui/Title";
import { WisnioProfileHighlightsSection } from "@/features/profile/components/WisnioProfileHighlightsSection";
import { CompetencyMapSection } from "@/shared/widgets/CompetencyMapSection";
import { PersonalityMapSection } from "@/shared/widgets/PersonalityMapSection";
import { ProfileValuesSection } from "@/features/profile/components/ProfileValuesSection";
import { useDimensionsWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ProfileSummarySection } from "@/features/profile/components/ProfileSummarySection";
import { MembersRow } from "@/shared/widgets/MembersRow";
import { Person } from "@/features/people/graphql/generated";
import ProfileLayout from "@/features/profile/components/ProfileLayout";
import { Breadcrumbs } from "@/shared/widgets/Breadcrumbs";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import { PDFExportTrigger } from "@/shared/pdf";
import { useRemoveMemberFromTeam } from "@/features/teams/hooks/useRemoveMember";
import ProfileSectionWrapper from "@/features/profile/components/ProfileLayout/ProfileSectionWrapper";
import { RoleType, AccessLevel } from "@/shared/constants";
import { getGptFlowKey } from "@/shared/constants/gpt-flow-types";
import { useTeamNotes } from "@/features/teams/hooks/useTeamNotes";
import { TeamProfilePdf } from "@/shared/pdf/Templates/Profiles";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { StepType, useTour } from "@reactour/tour";
import steps from "./_tour";
import { ResourceEnum } from "@/shared/constants/resources";
import { useTeamProfileData } from "@/features/teams/hooks/useTeamProfileData";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import {
  useProfileCompletion,
  useProfileLayoutProps,
} from "@/features/profile/hooks";
import { CompetencyInsights } from "@/shared/widgets/InsightsSection/CompetencyInsights";

function TeamMemberPointer(props: any) {
  return (
    <SubtitleText {...props}>
      <i className="fal fa-hand-pointer mr-2 " />
      Click on a team member to see their profile compared to the team.
    </SubtitleText>
  );
}

function TeamProfileScreen() {
  const { teamId: _teamId } = useParams();

  const { organization: selectedOrg } = useOrganisation();
  const teamId = toNumber(_teamId);

  const navigate = useOrgNavigate();

  const { validateAccessToResource } = useViewerPermissions();

  const {
    team,
    teamMembers,
    loading: loadingTeamData,
  } = useTeamProfileData(teamId);

  const { teams } = useTeams(selectedOrg.id!);

  const { setIsOpen, setSteps } = useTour();

  const handleOpenTour = () => {
    setSteps?.(steps as StepType[]);
    setIsOpen(true);
  };

  const surveyConfig = get(team, "config.dimensions", []);

  const canModifyTeam = validateAccessToResource({
    resourceType: ResourceEnum.team,
    accessLevel: AccessLevel.full,
    resourceId: teamId,
  });

  const { t } = useTranslation();

  const [openAddToTeamModal] = useModal(ModalFileName.ADD_PEOPLE_V2, {
    resource: ResourceEnum.team,
    resourceId: teamId,
    resourceName: team.name || "",
    defaultRole: RoleType.member,
    defaultAccessLevel: AccessLevel.read,
  });

  const [openConfirmModal] = useModal(ModalFileName.CONFIRM, {
    confirmLabel: t("common.confirm_remove"),
    cancelLabel: t("common.cancel_remove"),
    confirmButtonVariant: "destructive",
  });

  const { dimensionsData, loading: loadingDimensions } =
    useDimensionsWithScores(
      {
        team_id: toNumber(teamId),
        type: surveyConfig,
        isLoading: loadingTeamData || !team?.id,
      },
      teamMembers
    );

  const {
    notes,
    loading: loadingNotes,
    handleUpsertTeamNote,
    handleDeleteTeamNote,
  } = useTeamNotes(teamId);

  const teamSummary = find(
    notes,
    (n: any) => n.type === "summary" && n.person_id === null
  );

  const { handleRemoveMemberFromTeam } = useRemoveMemberFromTeam(teamId);

  const { personalityComplete, competenciesComplete, valuesComplete } =
    useProfileCompletion(dimensionsData);

  const handleMemberRemove = (person: Person) => {
    openConfirmModal({
      onConfirm: () => handleRemoveMemberFromTeam(person!.id!),
      title: t("team.confirm_remove_member", { name: person.name }),
    });
  };

  const { layoutProps, tagsData, scope, loadingMessage } =
    useProfileLayoutProps({
      contextType: "team_profile",
      entity: team,
      dimensionsData,
      surveyConfig,
      people: teamMembers as Person[],
      loadingStates: [loadingTeamData, loadingDimensions],
      additionalScope: { team_id: team.id! },
    });

  return (
    <ProfileLayout {...layoutProps} loadingMessage={loadingMessage}>
      <HeaderContent>
        {canModifyTeam && (
          <IconButton
            color="black"
            icon="Edit"
            label="Edit team"
            variant="outline"
            onClick={() => navigate(`/teams/${teamId}/settings`)}
          />
        )}
        <PDFExportTrigger
          documentTitle={`${team.name}`}
          TriggerComponent={(props) => (
            <IconButton
              color="black"
              icon="Print"
              label="Print team profile"
              variant="outline"
              {...props}
            />
          )}
          PDFComponent={
            <TeamProfilePdf
              team={team}
              members={teamMembers as Person[]}
              dimensions={dimensionsData}
              summary={teamSummary?.note || ""}
            />
          }
        />
        {true && ( // TODO: implement feature flag
          <IconButton
            color="black"
            icon="Info"
            label="Show team tour"
            variant="outline"
            onClick={handleOpenTour}
          />
        )}
      </HeaderContent>
      <Breadcrumbs
        items={[
          {
            label: team.name || "",
            options: map(teams, (_team) => ({
              label: _team?.name || "",
              path: `/teams/${_team?.id}`,
            })),
          },
        ]}
      />
      <Row className="justify-between">
        <Column className="gap-2">
          <h5 className="text-blue-500">{selectedOrg.name}</h5>
          <h2>{team.name}</h2>
        </Column>
        <Column className="shrink-0">
          <MembersRow
            className="max-w-[550px] justify-end teamMembers"
            teamId={teamId}
            members={teamMembers as Person[]}
            dimensionsConfig={surveyConfig}
            canAdd={canModifyTeam}
            onMemberAddClick={openAddToTeamModal}
            onMemberRemoveClick={handleMemberRemove}
            canRemove={canModifyTeam}
          />
          <TeamMemberPointer className="mt-4 text-right" />
        </Column>
      </Row>
      <WisnioProfileHighlightsSection dimensions={dimensionsData} />
      <ProfileSectionWrapper label="summary" id="summary">
        <ProfileSummarySection
          onDeleteSummary={() =>
            handleDeleteTeamNote(toNumber(teamSummary?.id), "summary")
          }
          onSaveSummary={(text) =>
            handleUpsertTeamNote({
              note: text,
              type: "summary",
              id: toNumber(teamSummary?.id),
            })
          }
          loading={loadingNotes}
          summaryContent={teamSummary?.note}
          tagsData={tagsData}
          updatedAt={teamSummary?.updated_at}
          flowId={getGptFlowKey("team_profile_summary")}
          scope={scope}
        />
      </ProfileSectionWrapper>

      {personalityComplete && (
        <ProfileSectionWrapper label="personality" id="personality">
          <PersonalityMapSection
            dimensions={dimensionsData.personality}
            context="team"
          />
        </ProfileSectionWrapper>
      )}

      {valuesComplete && (
        <ProfileSectionWrapper label="values" id="values">
          <ProfileValuesSection
            dimensions={dimensionsData.values}
            context="team"
          />
        </ProfileSectionWrapper>
      )}

      {competenciesComplete && (
        <ProfileSectionWrapper label="competencies" id="competencies">
          <CompetencyMapSection
            dimensions={dimensionsData.competencies}
            context="team"
          />
          <CompetencyInsights
            context="team"
            dimensions={dimensionsData?.competencies}
          />
        </ProfileSectionWrapper>
      )}

      {/* <ProfileSectionWrapper label="hiring" id="hiring"> */}
      <Column className="gap-8 mb-32">
        {/* <Title as="h3" description="Hiring suggestions for this team">
            Hiring
          </Title>
          <HiringSuggestions hiringSuggestions={team.hiringSuggestions!} /> */}
        <Title
          as="h4"
          description="Learn more about team members to see which values drive their motivation and how their personality contributes to the team's diversity."
          className="mt-8"
        >
          Members of the team
        </Title>
        <MembersRow
          teamId={teamId}
          members={teamMembers as Person[]}
          dimensionsConfig={surveyConfig}
          canAdd
          onMemberAddClick={openAddToTeamModal}
        />
        <TeamMemberPointer />
      </Column>
      {/* </ProfileSectionWrapper> */}
    </ProfileLayout>
  );
}

export default TeamProfileScreen;
